/*
 * <pmax-combo-media> — Shopify/Dawn already shows the variant's own assigned
 * image first when a variant is selected. When the two selected option
 * values match a configured combo (e.g. flavor + package type), this shows
 * ONLY that photo plus the combo's configured "back" and up to four optional
 * "extra" photos, hiding every other photo in the gallery (labels, other
 * combos' shots, etc.). Front/back/extras stay visible together regardless
 * of which one the shopper is currently looking at in the gallery.
 *
 * IMPORTANT: on a client-side variant change (no full page reload), the
 * theme replaces <variant-selects> with a brand new element (see
 * HTMLUpdateUtility.viewTransition in global.js) and renames its descendant
 * ids to dedupe them — so nothing here may cache a reference to it (or to
 * anything inside it). Every lookup below re-queries the DOM live by id.
 */
if (!customElements.get('pmax-combo-media')) {
  customElements.define(
    'pmax-combo-media',
    class PmaxComboMedia extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        const script = this.querySelector('script[data-combo-map]');
        try {
          this.map = JSON.parse(script?.textContent || '{}');
        } catch (error) {
          this.map = {};
        }

        this.onChange = (event) => {
          if (event.target.closest(`#variant-selects-${this.sectionId}`)) this.sync();
        };
        document.addEventListener('change', this.onChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data && event.data.sectionId && event.data.sectionId !== this.sectionId) return;
            this.sync();
          });
        }

        // Belt-and-braces: the theme also toggles which photo is "active" via
        // class/attribute changes on the (stable, never-replaced) gallery
        // lists — re-applying the same filter on those is harmless (idempotent).
        const mainList = this.mainList;
        const thumbList = this.thumbList;
        if (mainList || thumbList) {
          this.mediaObserver = new MutationObserver(() => this.sync());
          [mainList, thumbList].forEach((list) => {
            if (list) {
              this.mediaObserver.observe(list, {
                attributes: true,
                attributeFilter: ['class', 'aria-current'],
                subtree: true,
              });
            }
          });
        }

        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onChange);
        this.variantUnsubscriber?.();
        this.mediaObserver?.disconnect();
      }

      get variantSelects() {
        return document.getElementById(`variant-selects-${this.sectionId}`);
      }

      get mainList() {
        return document.getElementById(`Slider-Gallery-${this.sectionId}`);
      }

      get thumbList() {
        return document.getElementById(`Slider-Thumbnails-${this.sectionId}`);
      }

      selectedKey() {
        const variantSelects = this.variantSelects;
        if (!variantSelects) return null;

        const values = [];
        variantSelects.querySelectorAll('fieldset').forEach((fieldset) => {
          const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
          if (checkedRadio) {
            const label = fieldset.querySelector(`label[for="${CSS.escape(checkedRadio.id)}"]`);
            if (label?.dataset.valueHandle) values.push(label.dataset.valueHandle);
            return;
          }
          const select = fieldset.querySelector('select');
          if (select?.value) values.push(select.value);
        });

        if (values.length < 2) return null;
        return values.slice().sort().join('|');
      }

      // The variant's own front photo, read straight from the selected
      // variant's data (not from whichever slide currently has the
      // "is-active"/"aria-current" state, which changes on every thumbnail
      // click and would make front disappear once back is clicked).
      frontId() {
        const script = this.variantSelects?.querySelector('script[data-selected-variant]');
        if (!script) return null;
        try {
          const variant = JSON.parse(script.textContent || '{}');
          const mediaId = variant?.featured_media?.id;
          return mediaId ? `${this.sectionId}-${mediaId}` : null;
        } catch (error) {
          return null;
        }
      }

      sync() {
        const mainList = this.mainList;
        const thumbList = this.thumbList;

        // Always start from a clean slate: a previous combo's filtering must
        // never leak into the one now selected (e.g. a variant switch away
        // and back, or switching to a combo that isn't configured yet).
        this.showAll(mainList);
        this.showAll(thumbList);

        const key = this.selectedKey();
        const entry = key ? this.map[key] : null;
        if (!entry || !entry.back) return;

        const keepIds = [
          this.frontId(),
          entry.back,
          entry.extra,
          entry.extra2,
          entry.extra3,
          entry.extra4,
        ].filter(Boolean);

        this.filterAndOrder(mainList, keepIds, (el) => el.dataset.mediaId);
        this.filterAndOrder(thumbList, keepIds, (el) => el.dataset.target);

        document.getElementById(`GalleryViewer-${this.sectionId}`)?.resetPages?.();
        document.getElementById(`GalleryThumbnails-${this.sectionId}`)?.resetPages?.();
      }

      showAll(list) {
        if (!list) return;
        Array.from(list.children).forEach((el) => {
          el.style.display = '';
        });
      }

      filterAndOrder(list, keepIds, getId) {
        if (!list) return;

        const keepSet = new Set(keepIds);
        Array.from(list.children).forEach((el) => {
          el.style.display = keepSet.has(getId(el)) ? '' : 'none';
        });

        let anchor = null;
        keepIds.forEach((id) => {
          const el = Array.from(list.children).find((child) => getId(child) === id);
          if (!el) return;

          if (anchor) {
            if (el.previousElementSibling !== anchor) anchor.insertAdjacentElement('afterend', el);
          } else if (list.firstElementChild !== el) {
            list.insertBefore(el, list.firstElementChild);
          }
          anchor = el;
        });
      }
    }
  );
}
